// Retry utility for handling failed requests
export interface RetryOptions {
  maxRetries?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
  backoffMultiplier?: number;
  retryCondition?: (error: any) => boolean;
}

export class RetryService {
  /**
   * Retry a function with exponential backoff
   */
  async retry<T>(fn: () => Promise<T>, options: RetryOptions = {}): Promise<T> {
    const {
      maxRetries = 3,
      baseDelayMs = 1000,
      maxDelayMs = 10000,
      backoffMultiplier = 2,
      retryCondition = (error) => {
        // Retry on network errors, timeouts, and 5xx server errors
        if (error.code === "NETWORK_ERROR" || error.code === "TIMEOUT") {
          return true;
        }
        if (error.response?.status >= 500) {
          return true;
        }
        return false;
      },
    } = options;

    let lastError: any;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error;

        // Don't retry on the last attempt
        if (attempt === maxRetries) {
          break;
        }

        // Check if we should retry this error
        if (!retryCondition(error)) {
          break;
        }

        // Calculate delay with exponential backoff
        const delay = Math.min(
          baseDelayMs * Math.pow(backoffMultiplier, attempt),
          maxDelayMs
        );

        console.log(
          `Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms delay`
        );
        await this.delay(delay);
      }
    }

    throw lastError;
  }

  /**
   * Delay execution for a specified number of milliseconds
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Retry with different strategies based on error type
   */
  async retryWithStrategy<T>(
    fn: () => Promise<T>,
    errorType: "network" | "timeout" | "server" | "all" = "all"
  ): Promise<T> {
    const strategies = {
      network: {
        maxRetries: 5,
        baseDelayMs: 500,
        maxDelayMs: 5000,
        retryCondition: (error: any) =>
          error.code === "NETWORK_ERROR" ||
          error.message?.includes("Network Error") ||
          error.message?.includes("fetch"),
      },
      timeout: {
        maxRetries: 3,
        baseDelayMs: 1000,
        maxDelayMs: 8000,
        retryCondition: (error: any) =>
          error.code === "TIMEOUT" || error.message?.includes("timeout"),
      },
      server: {
        maxRetries: 2,
        baseDelayMs: 2000,
        maxDelayMs: 10000,
        retryCondition: (error: any) => error.response?.status >= 500,
      },
      all: {
        maxRetries: 3,
        baseDelayMs: 1000,
        maxDelayMs: 10000,
        retryCondition: (error: any) => {
          if (error.code === "NETWORK_ERROR" || error.code === "TIMEOUT") {
            return true;
          }
          if (error.response?.status >= 500) {
            return true;
          }
          if (
            error.message?.includes("timeout") ||
            error.message?.includes("Network Error")
          ) {
            return true;
          }
          return false;
        },
      },
    };

    return this.retry(fn, strategies[errorType]);
  }
}

// Export singleton instance
export const retryService = new RetryService();
export default retryService;


